import { call, put, select, takeLatest } from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import analytics from '@xto10x/core/lib/analytics';
import userApi from 'services/userApi';
import storage from '@xto10x/storage/lib';
import { StorageKey, UserStatus } from 'utils/constants';
import {
  hideAppLoading,
  saveUserInfoAction,
  showAlertMessage,
  showAppLoading,
  fetchAppData,
  saveOrgData,
  signOutAction,
  clearAllReducer,
  saveUserOrgsAction
} from 'containers/App/store/actions';
import { isNotEmptyString } from '@xto10x/core/lib/app.utils';
import { AnalyticsCategory } from 'utils/analytics.constants';
import { getOrgDataSelector } from '../../App/store/reducer';
import { actions, saveOrgSelectionAction } from './actions';
import { initialCall } from '../../App/store/actions';
import { redirectIfOnBoardedToPeopleCues } from '../../../utils/app.utils';

const getDefaultSelectedOrg = userOrgs => {
  try {
    const urlOrgSubDomain = window.location.pathname.split('/')[1];
    const matchingOrg = urlOrgSubDomain
      ? userOrgs.find(({ sub_domain }) => sub_domain === urlOrgSubDomain)
      : null;
    if (matchingOrg) return matchingOrg;
    if (userOrgs.length === 1) return userOrgs[0];
    if (
      storage.getDataFromLocal(StorageKey.ORG_INFO) != null &&
      storage.getDataFromLocal(StorageKey.ORG_INFO)?.sub_domain
    ) {
      const filteredOrgs = userOrgs.filter(
        org =>
          org.sub_domain ===
          storage.getDataFromLocal(StorageKey.ORG_INFO)?.sub_domain
      );

      storage.removeDataFromLocal(StorageKey.REDIRECT_URL);
      if (filteredOrgs.length === 1) return filteredOrgs[0];
    } else if (
      isNotEmptyString(storage.getDataFromLocal(StorageKey.REDIRECT_URL))
    ) {
      const urlOrgName = storage
        .getDataFromLocal(StorageKey.REDIRECT_URL)
        .split('/')[1];
      const filteredOrgs = userOrgs.filter(
        org => org.sub_domain === urlOrgName
      );
      if (filteredOrgs.length === 1) return filteredOrgs[0];
      storage.removeDataFromLocal(StorageKey.REDIRECT_URL);
    }
    return null;
  } catch (e) {
    return null;
  }
};

function* emailSignInRequestActionWorker() {
  try {
    const userInfo = yield userApi
      .getUserInfo()
      .then(res => res.data)
      .catch(error => {
        analytics.trackEvent(
          AnalyticsCategory.user_authentication,
          'sign_in_fail_email',
          `get_user_info_api_fail: ${error.message ? error.message : ''}`
        );

        analytics.trackError(
          'user_authentication_error: sign_in_fail_email: get_user_info_api_fail',
          error
        );
        return Promise.reject(error);
      });

    yield put(saveUserInfoAction(userInfo.user_info));
    yield put(saveUserOrgsAction(userInfo.orgs));
    analytics.setUserId(userInfo.user_info.user_reference_id);

    const currentSelectedOrg = yield select(getOrgDataSelector);
    const currentLocalSelectOrg = storage.getDataFromLocal(StorageKey.ORG_INFO);
    const orgInfo =
      currentLocalSelectOrg ||
      currentSelectedOrg ||
      getDefaultSelectedOrg(userInfo.orgs);
    if (orgInfo) {
      const isEmailLoginDisable = !!orgInfo?.cognito_auth_disabled;
      if (isEmailLoginDisable != null && isEmailLoginDisable === false) {
        analytics.trackEvent(
          AnalyticsCategory.user_authentication,
          'sign_in_success_email'
        );
        yield put(saveOrgSelectionAction(orgInfo, false));
      } else {
        analytics.trackEvent(
          AnalyticsCategory.user_authentication,
          'sign_in_fail_email',
          'email_authentication_blocked'
        );
        yield put(
          showAlertMessage(
            'error',
            `Email login is blocked by ${orgInfo.name}. Kindly use Google sign-in.`,
            null,
            null,
            10000
          )
        );
        storage.removeDataFromLocal(StorageKey.REDIRECT_URL);
        yield put(signOutAction(false));
        return;
      }
    } else {
      analytics.trackEvent(
        AnalyticsCategory.user_authentication,
        'sign_in_success_email'
      );
      yield put(replace('/orgs'));
    }
  } catch (error) {
    if (
      error.message === 'Please check your network connection and try again.'
    ) {
      yield put(showAlertMessage('error', error.message));
      return;
    }
    // Track analytics
    analytics.trackError(
      'user_authentication_error: sign_in_fail_email',
      error
    );
    analytics.trackEvent(
      AnalyticsCategory.user_authentication,
      'sign_in_fail_email',
      error
    );
    yield put(showAlertMessage('error', error.message));
    yield put(signOutAction(false));
  }
}

function* googleSignInRequestActionWorker() {
  try {
    yield put(showAppLoading());
    const userInfo = yield userApi
      .getUserInfo()
      .then(res => res.data)
      .catch(error => {
        analytics.trackError(
          'user_authentication_error: sign_in_fail_google: get_user_info_api_fail',
          error
        );
        return Promise.reject(error);
      });

    yield put(saveUserInfoAction(userInfo.user_info));
    yield put(saveUserOrgsAction(userInfo.orgs));
    analytics.setUserId(userInfo.user_info.user_reference_id);

    const defaultSelectedOrg = getDefaultSelectedOrg(userInfo.orgs);
    if (defaultSelectedOrg) {
      yield put(saveOrgSelectionAction(defaultSelectedOrg, false));
    } else {
      yield put(replace('/orgs'));
    }

    analytics.trackEvent(
      AnalyticsCategory.user_authentication,
      'sign_in_success_google'
    );
    yield put(initialCall());
    yield put(hideAppLoading());
  } catch (e) {
    if (e.message === 'Please check your network connection and try again.') {
      yield put(showAlertMessage('error', e.message));
      return;
    }
    // Track analytics
    analytics.trackError('user_authentication_error: sign_in_fail_google', e);
    analytics.trackEvent(
      AnalyticsCategory.user_authentication,
      'sign_in_fail_google',
      e
    );

    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    yield put(signOutAction(false));
  }
}

function* saveOrgSelectionActionWorker({
  org,
  shouldRedirectToHome,
  callback
}) {
  try {
    yield put(showAppLoading());
    yield put(clearAllReducer(false));

    redirectIfOnBoardedToPeopleCues(org);
    const user_reference_id = storage.getDataFromLocal(
      StorageKey.REQUESTER_USER_ID
    );

    // Save org data
    analytics.setCustomDimension('dimension2', org.name);
    yield put(saveOrgData(org));

    if (callback) callback();
    // Save user data
    analytics.setUserId(user_reference_id);
    let userInfo = yield userApi.updateUser({
      user_reference_id,
      status: UserStatus.ACTIVE
    });
    if (!userInfo.data) {
      userInfo = yield userApi.getUserInfo();
      yield put(saveUserInfoAction(userInfo.data.user_info));
    } else yield put(saveUserInfoAction(userInfo.data));

    if (shouldRedirectToHome)
      window.location.assign(`/${org.sub_domain}/dashboard`);
    // Fetch App data
    else yield put(fetchAppData());

    // yield put(replace(getHomeRedirectPath()));
    yield put(hideAppLoading());
  } catch (e) {
    // Track analytics
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message, null, null, 10000));
    analytics.trackError('save_org_selection_fail', e);
    analytics.trackEvent(
      AnalyticsCategory.user_authentication,
      'save_org_selection_fail',
      e
    );
  }
}

export default function* authenticationRequestsActionWatcher() {
  yield takeLatest(
    actions.EMAIL_SIGN_IN_REQUEST_ACTION,
    emailSignInRequestActionWorker
  );
  yield takeLatest(
    actions.GOOGLE_SIGN_IN_REQUEST_ACTION,
    googleSignInRequestActionWorker
  );
  yield takeLatest(
    actions.SAVE_ORG_SELECTION_ACTION,
    saveOrgSelectionActionWorker
  );
}
